// Create a new file: AdminMarketingReports.jsx

import { useState, useEffect } from 'react';
import { MapContainer, TileLayer, Marker, Popup } from "react-leaflet";
import L from 'leaflet';
import { FaEye, FaCalendarAlt, FaMapMarkerAlt, FaUser, FaFileAlt, FaTimes, FaDownload, FaFilter, FaRoute } from "react-icons/fa";
import UserDashboard from "./UserDashboard";
import marketingAPI from "../api/marketingAPI";
import "leaflet/dist/leaflet.css";
import { useNavigate } from "react-router-dom";
import { FaImage } from 'react-icons/fa';
import { useParams } from "react-router-dom";

// Fix for default marker icons
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: require('leaflet/dist/images/marker-icon-2x.png'),
  iconUrl: require('leaflet/dist/images/marker-icon.png'),
  shadowUrl: require('leaflet/dist/images/marker-shadow.png'),
});

// Custom icons for report locations
const originalIcon = new L.Icon({
  iconUrl: 'https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-2x-green.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png',
  iconSize: [25, 41],
  iconAnchor: [12, 41],
  popupAnchor: [1, -34],
  shadowSize: [41, 41]
});

const currentIcon = new L.Icon({
  iconUrl: 'https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-2x-red.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png',
  iconSize: [25, 41],
  iconAnchor: [12, 41],
  popupAnchor: [1, -34],
  shadowSize: [41, 41]
});

const AdminMarketingReports = () => {
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedReport, setSelectedReport] = useState(null);
  const [selectedPhoto, setSelectedPhoto] = useState(null);

  const [showMapModal, setShowMapModal] = useState(false);
  const [mapCenter, setMapCenter] = useState([11.1271, 78.6569]);
  const [filters, setFilters] = useState({
    startDate: '',
    endDate: '',
    assignedTo: ''
  });
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 10,
    total: 0,
    pages: 1
  });
  const navigate = useNavigate();
const { memberId } = useParams();
  // Fetch reports
  const fetchReports = async () => {
  setLoading(true);
  try {
    const token = localStorage.getItem("token");

    const params = {
      page: pagination.page,
      limit: pagination.limit,
      memberId, // 🔥 add this
      ...(filters.startDate && { startDate: filters.startDate }),
      ...(filters.endDate && { endDate: filters.endDate })
    };

    const response = await marketingAPI.get(
      "https://missionmap-lc.onrender.com/api/marketing-report/reports",
      {
        headers: { Authorization: `Bearer ${token}` },
        params
      }
    );

    if (response.data.success) {
      setReports(response.data.reports);
      setPagination(prev => ({
        ...prev,
        total: response.data.total,
        pages: response.data.pages
      }));
    }
  } catch (error) {
    console.error("Error fetching reports:", error);
    alert("Failed to load reports");
  } finally {
    setLoading(false);
  }
};

  useEffect(() => {
    fetchReports();
  }, [pagination.page, filters]);

  // View report on map
  const handleViewOnMap = (report) => {
    setSelectedReport(report);
    
    // Calculate center point between original and current locations
    const originalLat = report.originalLatitude || report.locationId?.latitude;
    const originalLng = report.originalLongitude || report.locationId?.longitude;
    const currentLat = report.currentLatitude;
    const currentLng = report.currentLongitude;
    
    if (originalLat && originalLng && currentLat && currentLng) {
      // Calculate midpoint
      const centerLat = (parseFloat(originalLat) + parseFloat(currentLat)) / 2;
      const centerLng = (parseFloat(originalLng) + parseFloat(currentLng)) / 2;
      setMapCenter([centerLat, centerLng]);
    } else if (originalLat && originalLng) {
      setMapCenter([originalLat, originalLng]);
    } else if (currentLat && currentLng) {
      setMapCenter([currentLat, currentLng]);
    }
    
    setShowMapModal(true);
  };

  // Export reports to CSV
  const exportToCSV = () => {
    const headers = [
      'Date', 'Time', 'Assigned To', 'Email', 
      'Original Location', 'Original Latitude', 'Original Longitude',
      'Current Location', 'Current Latitude', 'Current Longitude',
      'Distance (km)', 'Description'
    ];
    
    const csvData = reports.map(report => [
      new Date(report.reportDateTime).toLocaleDateString(),
      new Date(report.reportDateTime).toLocaleTimeString(),
      report.assignedTo?.name || 'N/A',
      report.assignedTo?.email || 'N/A',
      report.originalAddress || report.locationId?.address || 'N/A',
      report.originalLatitude || report.locationId?.latitude || 'N/A',
      report.originalLongitude || report.locationId?.longitude || 'N/A',
      report.currentAddress || 'N/A',
      report.currentLatitude || 'N/A',
      report.currentLongitude || 'N/A',
      report.distanceFromOriginal ? `${(report.distanceFromOriginal / 1000).toFixed(2)} km` : 'N/A',
      report.reportDescription || 'N/A'
    ]);

    const csvContent = [
      headers.join(','),
      ...csvData.map(row => row.map(cell => `"${cell}"`).join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `marketing-reports-${new Date().toISOString().split('T')[0]}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
  };

  // Format date for display
  const formatDateTime = (dateString) => {
    const date = new Date(dateString);
    return {
      date: date.toLocaleDateString('en-IN'),
      time: date.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' })
    };
  };

  // Calculate distance between two coordinates
  const calculateDistance = (lat1, lon1, lat2, lon2) => {
    const R = 6371; // Radius of the earth in km
    const dLat = (lat2 - lat1) * Math.PI / 180;
    const dLon = (lon2 - lon1) * Math.PI / 180;
    const a = 
      Math.sin(dLat/2) * Math.sin(dLat/2) +
      Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) * 
      Math.sin(dLon/2) * Math.sin(dLon/2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a));
    const distance = R * c; // Distance in km
    return distance.toFixed(2);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 p-4 md:p-6">
      <UserDashboard />
      
      {/* Header */}
      <div className="mb-8 flex justify-between items-center">
        <div>
          <div className="mb-20 text-gray-600"></div>
          <h1 className="text-2xl md:text-3xl font-bold text-gray-800 flex items-center gap-2">
            <FaFileAlt className="text-green-600" />
            Marketing Reports
          </h1>
          <p className="text-gray-600 mt-2">
            View all submitted marketing location reports
          </p>
        </div>

        {/* Back Button */}
        <button
          onClick={() => navigate("/marketing-members")}
          className="flex items-center gap-2 px-4 py-2 mt-20 bg-gray-800 text-white rounded-lg hover:bg-gray-900 transition"
        >
          ← Back
        </button>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-2xl shadow-lg p-5 mb-6">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-lg font-semibold text-gray-800 flex items-center gap-2">
            <FaFilter className="text-blue-500" />
            Filters
          </h2>
          <button
            onClick={exportToCSV}
            className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
          >
            <FaDownload />
            Export CSV
          </button>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Start Date</label>
            <input
              type="date"
              value={filters.startDate}
              onChange={(e) => setFilters(prev => ({ ...prev, startDate: e.target.value }))}
              className="w-full input border border-gray-300 rounded-lg px-3 py-2"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">End Date</label>
            <input
              type="date"
              value={filters.endDate}
              onChange={(e) => setFilters(prev => ({ ...prev, endDate: e.target.value }))}
              className="w-full input border border-gray-300 rounded-lg px-3 py-2"
            />
          </div>
          <div className="flex items-end">
            <button
              onClick={() => {
                setFilters({ startDate: '', endDate: '', assignedTo: '' });
                setPagination(prev => ({ ...prev, page: 1 }));
              }}
              className="w-full px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors"
            >
              Clear Filters
            </button>
          </div>
        </div>
      </div>

      {/* Reports Table */}
      <div className="bg-white rounded-2xl shadow-lg overflow-hidden">
        <div className="max-h-[500px] overflow-y-auto overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50 sticky top-0 z-10">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Date & Time</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Assigned To</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Original Location</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Current Location</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Distance</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Description</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {loading ? (
                <tr>
                  <td colSpan="7" className="px-6 py-8 text-center">
                    <div className="flex justify-center">
                      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                    </div>
                    <p className="mt-2 text-gray-600">Loading reports...</p>
                  </td>
                </tr>
              ) : reports.length === 0 ? (
                <tr>
                  <td colSpan="7" className="px-6 py-8 text-center text-gray-500">
                    No reports found
                  </td>
                </tr>
              ) : (
                reports.map((report) => {
                  const { date, time } = formatDateTime(report.reportDateTime);
                  const originalLat = report.originalLatitude || report.locationId?.latitude;
                  const originalLng = report.originalLongitude || report.locationId?.longitude;
                  const originalAddress = report.originalAddress || report.locationId?.address;
                  const currentAddress = report.currentAddress || 'Not available';
                  const currentLat = report.currentLatitude;
                  const currentLng = report.currentLongitude;
                  const description = report.reportDescription || 'No description';
                  
                  const distance = (originalLat && originalLng && currentLat && currentLng) 
                    ? calculateDistance(originalLat, originalLng, currentLat, currentLng)
                    : report.distanceFromOriginal 
                      ? (report.distanceFromOriginal / 1000).toFixed(2)
                      : 'N/A';

                  return (
                    <tr key={report._id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center gap-2">
                          <FaCalendarAlt className="text-blue-500" />
                          <div>
                            <div className="text-sm font-medium text-gray-900">{date}</div>
                            <div className="text-sm text-gray-500">{time}</div>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center gap-2">
                          <FaUser className="text-green-500" />
                          <div>
                            <div className="text-sm font-medium text-gray-900">
                              {report.assignedTo?.name || 'N/A'}
                            </div>
                            <div className="text-sm text-gray-500">
                              {report.assignedTo?.email || ''}
                            </div>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-2">
                          <FaMapMarkerAlt className="text-green-500" />
                          <div className="text-sm text-gray-900 max-w-xs truncate" title={originalAddress}>
                            {originalAddress}
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-2">
                          <FaMapMarkerAlt className="text-red-500" />
                          <div className="text-sm text-gray-900 max-w-xs truncate" title={currentAddress}>
                            {currentAddress}
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {distance !== 'N/A' ? (
                          <div className="flex items-center gap-1">
                            <FaRoute className="text-purple-500" />
                            {distance} km
                          </div>
                        ) : 'N/A'}
                      </td>
                      <td className="px-6 py-4">
                        <div className="text-sm text-gray-900 max-w-md truncate" title={description}>
                          {description}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium space-x-2">
  <button
    onClick={() => handleViewOnMap(report)}
    className="text-blue-600 hover:text-blue-900 flex items-center gap-1 px-3 py-1 bg-blue-50 rounded-lg hover:bg-blue-100 transition-colors"
  >
    <FaEye />
    Map
  </button>

  {report.photo && (
    <button
      onClick={() => setSelectedPhoto(report.photo)}
      className="text-green-600 hover:text-green-900 flex items-center gap-1 px-3 py-1 bg-green-50 rounded-lg hover:bg-green-100 transition-colors"
    >
      <FaImage />
      Photo
    </button>
  )}
</td>

                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {pagination.pages > 1 && (
          <div className="px-6 py-4 bg-gray-50 border-t border-gray-200">
            <div className="flex justify-between items-center">
              <div className="text-sm text-gray-700">
                Showing page {pagination.page} of {pagination.pages}
              </div>
              <div className="flex gap-2">
                <button
                  onClick={() => setPagination(prev => ({ ...prev, page: Math.max(1, prev.page - 1) }))}
                  disabled={pagination.page === 1}
                  className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-300"
                >
                  Previous
                </button>
                <button
                  onClick={() => setPagination(prev => ({ ...prev, page: Math.min(pagination.pages, prev.page + 1) }))}
                  disabled={pagination.page === pagination.pages}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-blue-700"
                >
                  Next
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
{selectedPhoto && (
  <div className="fixed inset-0 z-50 bg-black bg-opacity-90 flex items-center justify-center">
    
    {/* Close button */}
    <button
      onClick={() => setSelectedPhoto(null)}
      className="absolute top-4 right-4 text-white text-3xl z-50"
    >
      ×
    </button>

    {/* Image container */}
    <div className="w-full h-full flex items-center justify-center p-4">
      <img
        src={selectedPhoto}
        alt="Report"
        className="
          max-h-full 
          max-w-full 
          object-contain 
          rounded-lg 
          shadow-2xl
        "
      />
    </div>

  </div>
)}
      {/* Map Modal */}
      {showMapModal && selectedReport && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl w-full max-w-6xl max-h-[90vh] overflow-hidden">
            <div className="p-4 border-b border-gray-200 flex justify-between items-center">
              <div>
                <h3 className="text-lg font-semibold text-gray-800">Report Locations on Map</h3>
                <p className="text-sm text-gray-600">
                  {selectedReport.assignedTo?.name || 'User'} - {formatDateTime(selectedReport.reportDateTime).date}
                </p>
              </div>
              <button
                onClick={() => setShowMapModal(false)}
                className="p-2 hover:bg-gray-100 rounded-full"
              >
                <FaTimes size={20} />
              </button>
            </div>
            
            <div className="p-4">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-4">
                <div className="bg-green-50 p-3 rounded-lg">
                  <div className="text-sm text-green-700 font-medium flex items-center gap-2">
                    <FaMapMarkerAlt className="text-green-600" />
                    Original Location (Assigned)
                  </div>
                  <div className="mt-1 text-green-900">
                    {selectedReport.originalAddress || selectedReport.locationId?.address}
                  </div>
                  <div className="text-xs text-green-600 mt-1">
                    Coordinates: {selectedReport.originalLatitude?.toFixed(6) || selectedReport.locationId?.latitude?.toFixed(6)}, {selectedReport.originalLongitude?.toFixed(6) || selectedReport.locationId?.longitude?.toFixed(6)}
                  </div>
                </div>
                <div className="bg-red-50 p-3 rounded-lg">
                  <div className="text-sm text-red-700 font-medium flex items-center gap-2">
                    <FaMapMarkerAlt className="text-red-600" />
                    Current Location (Reported From)
                  </div>
                  <div className="mt-1 text-red-900">
                    {selectedReport.currentAddress || 'Not available'}
                  </div>
                  <div className="text-xs text-red-600 mt-1">
                    Coordinates: {selectedReport.currentLatitude?.toFixed(6)}, {selectedReport.currentLongitude?.toFixed(6)}
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-4">
                <div className="bg-blue-50 p-3 rounded-lg">
                  <div className="text-sm text-blue-700 font-medium">Assigned To</div>
                  <div className="mt-1 text-blue-900">
                    {selectedReport.assignedTo?.name} ({selectedReport.assignedTo?.email})
                  </div>
                </div>
                <div className="bg-purple-50 p-3 rounded-lg">
                  <div className="text-sm text-purple-700 font-medium">Distance Between Locations</div>
                  <div className="mt-1 text-purple-900">
                    {selectedReport.distanceFromOriginal 
                      ? `${(selectedReport.distanceFromOriginal / 1000).toFixed(2)} km`
                      : 'N/A'}
                  </div>
                </div>
                <div className="bg-yellow-50 p-3 rounded-lg">
                  <div className="text-sm text-yellow-700 font-medium">Report Description</div>
                  <div className="mt-1 text-yellow-900">
                    {selectedReport.reportDescription}
                  </div>
                </div>
              </div>

              <div className="h-[400px] rounded-xl overflow-hidden">
                <MapContainer
                  center={mapCenter}
                  zoom={selectedReport.originalLatitude && selectedReport.currentLatitude ? 5 : 15}
                  className="h-full w-full"
                  scrollWheelZoom={true}
                >
                  <TileLayer
                    attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                    url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                  />
                  
                  {/* Original Location Marker */}
                  {selectedReport.originalLatitude && selectedReport.originalLongitude && (
                    <Marker 
                      position={[selectedReport.originalLatitude, selectedReport.originalLongitude]} 
                      icon={originalIcon}
                    >
                      <Popup>
                        <div className="font-semibold text-green-700">Original Location</div>
                        <div className="text-sm mt-1">
                          {selectedReport.originalAddress || selectedReport.locationId?.address}
                        </div>
                        <div className="text-xs text-gray-500 mt-2">
                          {selectedReport.originalLatitude.toFixed(6)}, {selectedReport.originalLongitude.toFixed(6)}
                        </div>
                      </Popup>
                    </Marker>
                  )}

                  {/* Current Location Marker */}
                  {selectedReport.currentLatitude && selectedReport.currentLongitude && (
                    <Marker 
                      position={[selectedReport.currentLatitude, selectedReport.currentLongitude]} 
                      icon={currentIcon}
                    >
                      <Popup>
                        <div className="font-semibold text-red-700">Current Location</div>
                        <div className="text-sm mt-1">
                          {selectedReport.currentAddress}
                        </div>
                        <div className="text-xs text-gray-500 mt-2">
                          {selectedReport.currentLatitude.toFixed(6)}, {selectedReport.currentLongitude.toFixed(6)}
                        </div>
                      </Popup>
                    </Marker>
                  )}
                </MapContainer>

              </div>
            </div>
          </div>
        </div>
        
      )}
    </div>
  );
};

export default AdminMarketingReports;